import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDeploymentGroups } from '../../services/queries';
import { LoadingSpinner, ErrorAlert, EmptyState } from '../common';
import { DeploymentGroupDto } from '../../types/pipelineFlow';

export function Dashboard() {
  const [searchTerm, setSearchTerm] = useState('');
  const { data: deploymentGroups, isLoading, error } = useDeploymentGroups();

  const filteredGroups = deploymentGroups?.filter((group: DeploymentGroupDto) =>
    group.name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <ErrorAlert error={error} />;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold leading-6 text-gray-900">Pipeline Dashboard</h1>
          <p className="mt-2 text-sm text-gray-700">
            Monitor deployment groups and track pipeline runs across your environments.
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="mt-8">
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search deployment groups..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-input max-w-md"
          />
        </div>

        {/* Deployment Groups */}
        {filteredGroups.length === 0 ? (
          <EmptyState
            title="No deployment groups found"
            description={searchTerm ? "Try adjusting your search terms." : "No deployment groups are available."}
            icon={
              <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125S3.75 19.903 3.75 17.625V6.375" />
              </svg>
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredGroups.map((group) => (
              <DashboardGroupCard key={group.id} group={group} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface DashboardGroupCardProps {
  group: DeploymentGroupDto;
}

function DashboardGroupCard({ group }: DashboardGroupCardProps) {
  return (
    <div className="relative flex flex-col rounded-lg border border-gray-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <Link
            to={`/admin/deployment-groups/${group.id}`}
            className="text-lg font-semibold text-gray-900 hover:text-blue-600"
          >
            {group.name}
          </Link>
          <p className="mt-1 text-sm text-gray-500">
            Created {new Date(group.createdOn).toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center">
          <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
            {group.pipelineCount} pipelines
          </span>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
        <div>
          Last modified {new Date(group.lastModifiedOn).toLocaleDateString()}
        </div>
        <Link
          to={`/admin/deployment-groups/${group.id}`}
          className="font-medium text-blue-600 hover:text-blue-500"
        >
          View Pipelines →
        </Link>
      </div>
    </div>
  );
}